// Market — Signals › Market (2026-10-07): what is selling in our trades,
// where, and who is buying.
//
// One list behind it (app/market_view.py): market_transactions (what the
// web said — trade sweeps and acquired-company signals) and confirmed
// company_acquisitions (our own companies, bought), one row per target and
// acquirer. Filters: trade, state, period; undated deals stay in the counts.
// The acquirers table places every buyer — on our roster, an Atlas company,
// already a candidate, or unknown to us — and "Add as buyer candidate" sends
// an unknown one to Buyers › Candidates, where Accept puts it on the roster.

const MARKET_PERIODS = [[12, '12 months'], [24, '24 months'], [0, 'All time']];
const ACQ_STATUS = {
  roster: { label: 'On our roster', hue: 'green' },
  atlas: { label: 'Atlas company', hue: 'blue' },
  candidate: { label: 'Candidate', hue: 'amber' },
  unknown: { label: 'Not in Atlas', hue: 'slate' },
};
const mktDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : 'date unknown');
const mktNum = (n) => (n == null ? '—' : Number(n).toLocaleString());

// Horizontal bars: label · count, the bar scaled to the largest row.
const MarketBars = ({ title, rows, label, onPick, picked }) => {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div style={{ flex: 1, minWidth: 220, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', padding: '12px 14px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>{title}</div>
      {rows.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Nothing in this period.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {rows.map((r) => {
          const key = label(r);
          const active = picked && picked === r.value;
          return (
            <button key={key} type="button" onClick={() => onPick && onPick(active ? '' : r.value)}
              title={onPick ? (active ? 'Clear this filter' : `Only ${key}`) : undefined}
              style={{ all: 'unset', cursor: onPick ? 'pointer' : 'default', display: 'grid', gridTemplateColumns: 'minmax(90px, 1fr) 2fr 40px', gap: 8, alignItems: 'center', fontSize: 12 }}>
              <span style={{ color: active ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: active ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{key}</span>
              <span style={{ height: 8, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden' }}>
                <span style={{ display: 'block', height: '100%', width: `${Math.max(3, Math.round((r.n / max) * 100))}%`, background: active ? 'var(--accent)' : 'var(--text-4)', borderRadius: 4 }} />
              </span>
              <span className="num" style={{ color: 'var(--text-3)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{mktNum(r.n)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Eight quarters, dated deals only.
const MarketQuarters = ({ data, undated }) => {
  const max = Math.max(1, ...data.map((q) => q.n));
  return (
    <div style={{ flex: 1.4, minWidth: 280, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Sales by quarter</span>
        {undated > 0 && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{mktNum(undated)} undated not shown</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
        {data.map((q) => (
          <div key={q.quarter} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: 4 }}>
            <span className="num" style={{ fontSize: 11, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{q.n || ''}</span>
            <div style={{ width: '70%', height: `${Math.max(2, Math.round((q.n / max) * 84))}px`, background: 'var(--accent)', opacity: q.n ? 0.85 : 0.25, borderRadius: '4px 4px 0 0' }} />
            <span style={{ fontSize: 10.5, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{q.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketKpi = ({ label, value, sub }) => (
  <div style={{ flex: 1, minWidth: 140, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', padding: '10px 14px' }}>
    <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
    <div className="num" style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{sub}</div>}
  </div>
);

const AcquirerRow = ({ a, goToCompany, onDeals, onCandidate, busy, needDomain, onDomain }) => {
  const st = ACQ_STATUS[a.status] || ACQ_STATUS.unknown;
  const [domain, setDomain] = React.useState('');
  const openBuyer = () => window.AtlasNav?.goToBuyer?.(a.buyer_id);
  const openCompany = () => (goToCompany ? goToCompany(a.company_id) : window.AtlasNav?.goToCompany?.(a.company_id));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.6fr) 60px minmax(140px, 1.4fr) minmax(90px, 0.8fr) 90px 120px 190px', gap: 10, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: 12.5 }}>
      <div style={{ minWidth: 0 }}>
        {a.buyer_id ? (
          <button type="button" onClick={openBuyer} style={{ all: 'unset', cursor: 'pointer', fontWeight: 600, color: 'var(--text)' }}>{a.name}</button>
        ) : a.company_id ? (
          <button type="button" onClick={openCompany} style={{ all: 'unset', cursor: 'pointer', fontWeight: 600, color: 'var(--text)' }}>{a.name}</button>
        ) : <span style={{ fontWeight: 600, color: 'var(--text)' }}>{a.name}</span>}
        {a.atlas_targets > 0 && <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{a.atlas_targets} of ours</div>}
      </div>
      <button type="button" onClick={() => onDeals(a)} className="num" title="Only this buyer's deals below"
        style={{ all: 'unset', cursor: 'pointer', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: 'var(--accent-text)' }}>{a.deals}</button>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(a.industries || []).slice(0, 3).map((i) => <window.Pill key={i} hue="slate" sz="sm">{i}</window.Pill>)}
        {(a.industries || []).length > 3 && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>+{a.industries.length - 3}</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{(a.states || []).slice(0, 6).join(' ')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{mktDate(a.last_sold_on)}</div>
      <div><window.Pill hue={st.hue} sz="sm">{a.status === 'candidate' && a.candidate ? `Candidate · ${a.candidate.status}` : st.label}</window.Pill></div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
        {a.status === 'roster' && <window.Btn size="xs" variant="ghost" onClick={openBuyer}>Open buyer</window.Btn>}
        {a.status === 'candidate' && a.candidate && (
          <a href="#/buyers?tab=candidates" style={{ fontSize: 12, color: 'var(--accent-text)' }}>Buyers › Candidates</a>
        )}
        {(a.status === 'unknown' || a.status === 'atlas') && needDomain !== a.key && (
          <window.Btn size="xs" variant="accent" disabled={busy === a.key} onClick={() => onCandidate(a)}
            title="Send this acquirer to Buyers › Candidates with its deals as the evidence">
            {busy === a.key ? '…' : 'Add as buyer candidate'}
          </window.Btn>
        )}
        {needDomain === a.key && (
          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <input autoFocus value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="their website"
              onKeyDown={(e) => { if (e.key === 'Enter' && domain.trim()) onDomain(a, domain.trim()); if (e.key === 'Escape') onDomain(a, null); }}
              style={{ width: 140, fontSize: 12, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }} />
            <window.Btn size="xs" variant="accent" disabled={!domain.trim() || busy === a.key} onClick={() => onDomain(a, domain.trim())}>Add</window.Btn>
          </span>
        )}
      </div>
    </div>
  );
};

const MarketView = ({ goToCompany }) => {
  const [industry, setIndustry] = React.useState('');
  const [state, setState] = React.useState('');
  const [months, setMonths] = React.useState(24);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [acquirer, setAcquirer] = React.useState(null);     // {key, name} — the list shows only this buyer's deals
  const [tx, setTx] = React.useState(null);
  const [txMore, setTxMore] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  const [needDomain, setNeedDomain] = React.useState(null);
  const PAGE = 50;

  const params = React.useMemo(() => {
    const p = { months };
    if (industry) p.industry_id = industry;
    if (state) p.state = state;
    return p;
  }, [industry, state, months]);

  const loadSummary = React.useCallback(() => {
    window.AtlasApi.marketSummary(params)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { setError(e.message); setData((d) => d || null); });
  }, [params]);
  const loadTx = React.useCallback((offset = 0) => {
    const p = { ...params, limit: PAGE, offset };
    if (acquirer) p.acquirer = acquirer.key;
    return window.AtlasApi.marketTransactions(p)
      .then((r) => {
        const items = r.items || [];
        setTx((prev) => (offset ? [...(prev || []), ...items] : items));
        setTxMore(items.length >= PAGE);
      })
      .catch(() => { if (!offset) setTx([]); });
  }, [params, acquirer]);
  React.useEffect(() => { loadSummary(); }, [loadSummary]);
  React.useEffect(() => { setTx(null); loadTx(0); }, [loadTx]);

  const addCandidate = async (a, domain) => {
    setBusy(a.key);
    try {
      const body = { acquirer_name: a.name };
      if (domain) body.domain = domain;
      const r = await window.AtlasApi.marketAddBuyerCandidate(body);
      setNeedDomain(null);
      const title = r.status === 'on_roster' ? `${a.name} is already on the roster`
        : r.status === 'exists' ? `${a.name} is already a candidate`
          : `${a.name} sent to Buyers › Candidates`;
      pushToast({ type: 'success', title, body: r.domain ? r.domain : undefined });
      loadSummary();
    } catch (e) {
      if (/no_website/.test(e.message || '')) { setNeedDomain(a.key); return; }
      pushToast({ type: 'error', title: 'Could not add the candidate', body: e.message });
    } finally { setBusy(null); }
  };

  const chip = (active) => ({ fontSize: 12, padding: '4px 10px', borderRadius: 999, border: '1px solid', cursor: 'pointer',
    borderColor: active ? 'var(--accent)' : 'var(--border)', background: active ? 'var(--accent-soft)' : 'transparent', color: active ? 'var(--accent-text)' : 'var(--text-3)' });
  const T = (data && data.totals) || {};
  const opts = (data && data.options) || { industries: [], states: [] };

  return (
    <div>
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', padding: '8px 0 10px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <window.Picker value={industry} onChange={setIndustry} emptyLabel="All trades" placeholder="Trade" minWidth={150}
          options={opts.industries.map((i) => ({ value: String(i.industry_id), label: i.industry_name, count: i.n }))} />
        <window.Picker value={state} onChange={setState} emptyLabel="All states" placeholder="State" minWidth={130}
          options={opts.states.map((s) => ({ value: s.state, label: s.state, count: s.n }))} />
        <div style={{ display: 'flex', gap: 4 }}>
          {MARKET_PERIODS.map(([m, label]) => <button key={m} type="button" style={chip(months === m)} onClick={() => setMonths(m)}>{label}</button>)}
        </div>
        {acquirer && (
          <button type="button" onClick={() => setAcquirer(null)} style={{ ...chip(true), marginLeft: 'auto' }} title="Show every buyer's deals again">
            {acquirer.name} · clear
          </button>
        )}
      </div>

      {error && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--rose-fg)' }}>{error}</div>}
      {!data && !error && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-4)' }}>Loading…</div>}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
            <MarketKpi label="Transactions" value={mktNum(T.n)} sub={`${mktNum(T.dated)} dated · ${mktNum((T.n || 0) - (T.dated || 0))} undated`} />
            <MarketKpi label="Acquirers" value={mktNum(T.acquirers)} sub={`${mktNum(T.roster_acquirers)} on our roster`} />
            <MarketKpi label="Atlas companies sold" value={mktNum(T.atlas_targets)} sub={`${mktNum(T.from_atlas)} from our records`} />
            <MarketKpi label="States" value={mktNum(T.states)} />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <MarketQuarters data={data.by_quarter || []} undated={(T.n || 0) - (T.dated || 0)} />
            <MarketBars title="By trade" rows={(data.by_industry || []).map((r) => ({ ...r, value: r.industry_id || '' }))}
              label={(r) => r.industry_name} onPick={(v) => setIndustry(v)} picked={industry} />
            <MarketBars title="By state" rows={(data.by_state || []).map((r) => ({ ...r, value: r.state }))}
              label={(r) => r.state} onPick={(v) => setState(v)} picked={state} />
          </div>

          <div style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1.6fr) 60px minmax(140px, 1.4fr) minmax(90px, 0.8fr) 90px 120px 190px', gap: 10, padding: '8px 12px', fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, alignItems: 'center' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                Acquirer
                {window.HelpTip && <window.HelpTip text="Every buyer behind the transactions in this period, ranked by deals. On our roster = a buyer we already send deals to; Atlas company = we have the record but not as a buyer; Not in Atlas = add it as a candidate." />}
              </span>
              <span>Deals</span><span>Trades</span><span>States</span><span>Last</span><span>Status</span><span />
            </div>
            {(data.acquirers || []).length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)', borderTop: '1px solid var(--border)' }}>No acquirers in this period.</div>}
            {(data.acquirers || []).map((a) => (
              <AcquirerRow key={a.key} a={a} goToCompany={goToCompany} busy={busy} needDomain={needDomain}
                onDeals={(x) => setAcquirer(acquirer && acquirer.key === x.key ? null : { key: x.key, name: x.name })}
                onCandidate={(x) => addCandidate(x)}
                onDomain={(x, d) => (d ? addCandidate(x, d) : setNeedDomain(null))} />
            ))}
          </div>

          <div style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
              {acquirer ? `${acquirer.name} · deals` : 'Recent transactions'}
            </div>
            {tx === null && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-4)', borderTop: '1px solid var(--border)' }}>Loading…</div>}
            {tx && tx.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-3)', borderTop: '1px solid var(--border)' }}>Nothing in this period.</div>}
            {(tx || []).map((t) => (
              <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '90px minmax(160px, 1.4fr) minmax(160px, 1.4fr) minmax(120px, 1fr) 150px', gap: 10, alignItems: 'center', padding: '7px 12px', borderTop: '1px solid var(--border)', fontSize: 12.5 }}>
                <span style={{ color: t.sold_on ? 'var(--text-3)' : 'var(--text-4)', fontSize: 12 }}>{mktDate(t.sold_on)}</span>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.company_id
                    ? <button type="button" onClick={() => (goToCompany ? goToCompany(t.company_id) : window.AtlasNav?.goToCompany?.(t.company_id))}
                        style={{ all: 'unset', cursor: 'pointer', fontWeight: 600, color: 'var(--text)' }}>{t.target_name}</button>
                    : <span style={{ fontWeight: 600, color: 'var(--text)' }}>{t.target_name}</span>}
                  {t.source === 'atlas' && <window.Pill hue="blue" sz="sm" title="One of our companies">ours</window.Pill>}
                </span>
                <span style={{ color: 'var(--text-2)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>to {t.acquirer_name}</span>
                <span style={{ color: 'var(--text-3)', fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[t.industry_name, [t.target_city, t.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-4)', textAlign: 'right' }}>
                  {t.price_usd ? <span className="num" style={{ marginRight: 8, color: 'var(--text-3)' }}>{window.fmtMoney ? window.fmtMoney(t.price_usd) : t.price_usd}</span> : null}
                  {t.source_url && <a href={t.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{t.source_url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0].slice(0, 28)}</a>}
                </span>
              </div>
            ))}
            {tx && tx.length > 0 && txMore && (
              <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)' }}>
                <window.Btn size="xs" variant="ghost" onClick={() => loadTx(tx.length)}>Show more</window.Btn>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

Object.assign(window, { MarketView });
